import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { HERO_SLIDES, SITE } from "../data/site";

const AUTO_MS = 6500;

export default function HeroSlider() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = HERO_SLIDES.length;

  const goTo = useCallback((i) => setIdx(((i % count) + count) % count), [count]);
  const next = useCallback(() => goTo(idx + 1), [idx, goTo]);
  const prev = useCallback(() => goTo(idx - 1), [idx, goTo]);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => {
      if (typeof document === "undefined" || document.visibilityState === "visible") {
        next();
      }
    }, AUTO_MS);
    return () => clearInterval(t);
  }, [next, paused]);

  const slide = HERO_SLIDES[idx];

  return (
    <section
      className="relative w-full overflow-hidden bg-slate-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="hero-slider"
    >
      <div className="relative h-[78vh] min-h-[560px] max-h-[820px] w-full">
        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.02 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0"
          >
            <img
              src={slide.image}
              alt={slide.title}
              loading={idx === 0 ? "eager" : "lazy"}
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* gradient mask for legibility */}
            <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/70 to-slate-950/30" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-transparent to-transparent" />
          </motion.div>
        </AnimatePresence>

        {/* Content */}
        <div className="relative h-full container-st flex items-center">
          <div className="max-w-3xl text-white">
            <motion.div
              key={`eyebrow-${idx}`}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/15 text-xs font-bold tracking-widest uppercase text-blue-200"
            >
              <Sparkles className="w-3.5 h-3.5" /> {slide.eyebrow}
            </motion.div>
            <motion.h1
              key={`title-${idx}`}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-6 text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[1.05]"
            >
              {slide.title}
            </motion.h1>
            <motion.p
              key={`sub-${idx}`}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-6 text-lg sm:text-xl text-slate-200 max-w-2xl leading-relaxed"
            >
              {slide.subtitle}
            </motion.p>
            <motion.div
              key={`cta-${idx}`}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-8 flex flex-wrap items-center gap-3"
            >
              <Link
                to={slide.cta.to}
                data-testid={`hero-slide-cta-${idx}`}
                className="btn-shine inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3.5 rounded-full"
              >
                {slide.cta.label} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact?type=quote"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white font-semibold px-6 py-3.5 rounded-full"
              >
                Get a Job Quote
              </Link>
            </motion.div>

            <div className="mt-10 flex items-center gap-6 text-xs uppercase tracking-widest text-slate-300/80">
              <span>Since {SITE.yearStarted}</span>
              <span className="w-1 h-1 rounded-full bg-white/40" />
              <span>Incorporated {SITE.yearIncorporated}</span>
              <span className="w-1 h-1 rounded-full bg-white/40" />
              <span>{SITE.rc}</span>
            </div>
          </div>
        </div>

        {/* Prev / Next */}
        <button
          onClick={prev}
          aria-label="Previous slide"
          data-testid="hero-prev"
          className="absolute left-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur border border-white/15 grid place-items-center text-white"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={next}
          aria-label="Next slide"
          data-testid="hero-next"
          className="absolute right-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur border border-white/15 grid place-items-center text-white"
        >
          <ChevronRight className="w-5 h-5" />
        </button>

        {/* Dots */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2.5" data-testid="hero-dots">
          {HERO_SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              data-testid={`hero-dot-${i}`}
              aria-label={`Slide ${i + 1}`}
              className={`transition-all rounded-full ${
                i === idx ? "w-10 h-2 bg-white" : "w-2 h-2 bg-white/40 hover:bg-white/70"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
